import { Controller } from '@hotwired/stimulus'

/**
 * AWS WAF Form Controller
 *
 * Ensures AWS WAF token is acquired before form submission for ACFP/ATP protected endpoints.
 *
 * The AWS WAF JavaScript SDK runs silently in the background to collect browser telemetry.
 * For form submissions to protected endpoints (e.g., registration, login), we must ensure
 * the token is available before the form is submitted.
 *
 * Usage:
 *   <form data-controller="aws-waf-form" action="/users" method="post">
 *     ...
 *   </form>
 *
 * Optional targets:
 *   - submitButton: Will be disabled while acquiring token
 *
 * Reference: https://docs.aws.amazon.com/waf/latest/developerguide/waf-js-challenge-api-get-token.html
 */
export default class extends Controller {
  static targets = ['submitButton']

  connect() {
    // Initialize flags on every connection for clean state (defense-in-depth)
    // This ensures no stale flags persist across Turbo navigation reconnections
    this.acquiringToken = false
    this.tokenAcquisitionAttempted = false

    // Check if AWS WAF Integration is available
    if (!this.isAwsWafAvailable()) {
      // AWS WAF SDK not loaded - likely not enabled in this environment
      return
    }

    // Store bound handler reference for proper cleanup in disconnect()
    this.boundHandleSubmit = this.handleSubmit.bind(this)
    this.element.addEventListener('submit', this.boundHandleSubmit)
  }

  disconnect() {
    if (this.boundHandleSubmit) {
      this.element.removeEventListener('submit', this.boundHandleSubmit)
    }
    // Reset state on disconnect to prevent stale flags if controller reconnects
    this.acquiringToken = false
    this.tokenAcquisitionAttempted = false
  }

  /**
   * Intercepts form submission to ensure AWS WAF token is available.
   * If token is not present, prevents submission and acquires token first.
   */
  handleSubmit(event) {
    if (!this.isAwsWafAvailable()) {
      return // Allow normal submission if WAF not available
    }

    // Check if token is already present
    if (window.AwsWafIntegration.hasToken()) {
      return // Token exists, allow normal submission
    }

    // Skip if we've already attempted token acquisition (prevents infinite loop on failure)
    if (this.tokenAcquisitionAttempted) {
      return // Allow submission without token - server-side WAF will handle it
    }

    // Prevent duplicate submissions during async token acquisition
    // (button disable is visual-only, doesn't prevent Enter key or programmatic submit)
    if (this.acquiringToken) {
      event.preventDefault()
      return
    }

    // Prevent form submission until we have a token
    event.preventDefault()

    // Mark acquisition in progress and attempted
    this.acquiringToken = true
    this.tokenAcquisitionAttempted = true

    // Disable submit button to prevent double-clicks
    this.disableSubmitButton()

    // Acquire token then submit (with timeout to prevent permanent form breakage)
    this.getTokenWithTimeout()
      .then(() => {
        // Token acquired, re-enable button and submit the form
        // Re-enabling first ensures that if HTML5 validation fails, the user can retry
        this.acquiringToken = false
        this.enableSubmitButton()
        this.submitForm()
      })
      .catch((error) => {
        // Token acquisition failed - log but still allow submission
        // The server-side WAF rules will handle requests without valid tokens
        console.warn('AWS WAF token acquisition failed:', error)
        this.acquiringToken = false
        this.enableSubmitButton()
        this.submitForm()
      })
  }

  /**
   * Submits the form with Safari < 16 compatibility.
   * requestSubmit() triggers validation but isn't supported in older Safari.
   * Falls back to clicking submit button (also triggers validation) or submit().
   */
  submitForm() {
    if (typeof this.element.requestSubmit === 'function') {
      this.element.requestSubmit()
    } else if (this.hasSubmitButtonTarget) {
      // Safari < 16 fallback: click submit button to trigger validation
      this.submitButtonTarget.click()
    } else {
      // Last resort: submit() bypasses validation but works everywhere
      this.element.submit()
    }
  }

  /**
   * Wraps getToken() with a timeout to prevent permanent form breakage
   * if the SDK hangs. AWS SDK has internal 2s timeout, but this is defense-in-depth.
   */
  getTokenWithTimeout(timeoutMs = 5000) {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Token acquisition timeout')), timeoutMs)
    )
    return Promise.race([window.AwsWafIntegration.getToken(), timeoutPromise])
  }

  /**
   * Checks if AWS WAF Integration SDK is loaded and available.
   */
  isAwsWafAvailable() {
    return typeof window.AwsWafIntegration !== 'undefined' &&
           typeof window.AwsWafIntegration.hasToken === 'function' &&
           typeof window.AwsWafIntegration.getToken === 'function'
  }

  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      // Handle both <button> (textContent) and <input type="submit"> (value)
      this.isInputElement = this.submitButtonTarget.tagName === 'INPUT'
      this.originalButtonText = this.isInputElement
        ? this.submitButtonTarget.value
        : this.submitButtonTarget.textContent
      if (this.isInputElement) {
        this.submitButtonTarget.value = 'Please wait...'
      } else {
        this.submitButtonTarget.textContent = 'Please wait...'
      }
    }
  }

  enableSubmitButton() {
    try {
      // Always re-enable if we have the target - don't rely on originalButtonText being truthy
      // since it could legitimately be empty for some button configurations
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
        if (this.originalButtonText !== undefined) {
          if (this.isInputElement) {
            this.submitButtonTarget.value = this.originalButtonText
          } else {
            this.submitButtonTarget.textContent = this.originalButtonText
          }
        }
      }
    } catch (error) {
      // Element may have been removed from DOM during async token acquisition
      console.warn('Failed to re-enable submit button:', error)
    }
  }
}
